<template>
    <div class="supplier-commitments-form">
        <v-form
            ref="form"
            v-model="validation.isValid"
            class="supplier-commitments-form__form"
            @submit.prevent
        >
            <div
                v-for="(field, index) in fields"
                v-show="!field.hidden"
                :id="`sc_${editContext ? editContext._id : 'new'}_field_${index}`"
                :key="`sc_${editContext ? editContext._id : 'new'}_field_${index}`"
                :class="
                    `supplier-commitments-form__grid-cell supplier-commitments-form__grid-cell-${field.type ||
                        'default'}`
                "
            >
                <div v-if="field.value">
                    {{ field.value }}
                </div>
                <component
                    v-bind="field"
                    :is="getVuexComponent(field.type)"
                    v-else-if="field.type && !field.expandedAreaComponent"
                    :vuex-module="vuexModule"
                    :namespace="computedNamespace"
                    :context="context"
                    :disabled="isDisabledField({ editable: isFieldEnabled(field) })"
                    v-on="
                        constructVBindEventObj({
                            events: getAttribute(field, 'events'),
                            context: editContext,
                        })
                    "
                />
                <div v-else-if="field.functionName">
                    <span>{{ callFieldAction(field.functionName, editContext) }}</span>
                </div>
                <span v-else>{{ getAttribute(editContext, field.name) }}</span>
            </div>
            <div class="supplier-commitments-form__grid-cell">
                <notes
                    :notes="notes"
                    :disabled="!editContext"
                    open-left
                    @delete="removeNote"
                    @post="postNote"
                    @save="saveNote"
                />
            </div>
            <div class="supplier-commitments-form__grid-cell">
                <main-expand-button :is-expanded="true" size="small" @expand="toggleExpandedArea" />
            </div>
            <div v-if="isPromotionSupplierFundingView" class="supplier-commitments-form__grid-cell">
                <allocation-checkbox
                    :supplier-commitment="editContext"
                    :promotion-namespace="promotionNamespace"
                />
            </div>
            <div class="supplier-commitments-form__expanded-area">
                <div
                    v-for="field in expandedAreaFields"
                    :key="field.fieldName"
                    class="supplier-commitments-form__expanded-component"
                >
                    <component
                        v-bind="field"
                        :is="getVuexComponent(field.type)"
                        :ref="field.ref"
                        :vuex-module="vuexModule"
                        :namespace="computedNamespace"
                        :context="context"
                        v-on="
                            constructVBindEventObj({
                                events: getAttribute(field, 'events'),
                                context: editContext,
                            })
                        "
                    />
                </div>
            </div>
        </v-form>
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import NotesFieldKeys from '@enums/notes-field-keys';
import { sortBy, isEmpty, get, map, join } from 'lodash';
import measurements from '@enums/measurements';
import configDrivenGridComponentMixin from '@/js/mixins/config-driven-grid-component';
import vuexFormMixin from '@/js/mixins/vuex-form';

export default {
    mixins: [vuexFormMixin, configDrivenGridComponentMixin],
    props: {
        expandedAreaFields: {
            type: Array,
            default: () => [],
        },
        isPromotionSupplierFundingView: {
            type: Boolean,
            default: false,
        },
        promotionNamespace: String,
    },
    emits: ['toggle', 'supplierNameChange', 'matrix-updated'],
    computed: {
        ...mapGetters('suppliers', ['categoryFromFilter']),

        notes() {
            return this.editContext ? sortBy(this.editContext.notes, 'createdOn') : [];
        },
        allocatedPromotionIds() {
            return this.editContext ? this.editContext.allocatedPromotionIds : [];
        },
    },
    watch: {
        allocatedPromotionIds() {
            this.$refs.commitmentMatrix[0].updateMeasurementCounts();
        },
    },
    methods: {
        ...mapActions('supplierCommitments', [
            'setStagingAreaField',
            'addNote',
            'deleteNote',
            'updateNote',
        ]),
        // Helper for value-only function fields; preserves mixin callAction used by event bindings.
        callFieldAction(methodName, context) {
            if (typeof this[methodName] === 'function') {
                return this[methodName](context);
            }
            console.error(`Method '${methodName}' not found on component`);
            return '';
        },
        isFieldEnabled(field) {
            // make the restricted field disabled if the supplier commitment have promotions allocated to it.
            return isEmpty(get(this.editContext, 'allocatedPromotionIds', [])) || !field.restricted;
        },
        setField(fieldName, value) {
            this.setStagingAreaField({
                namespace: this.computedNamespace,
                fieldName,
                value,
            });
        },
        toggleExpandedArea() {
            this.$emit('toggle');
        },

        getTotalCurrentAllocations({
            total = 0,
            productCurrent = 0,
            promotionCurrent = 0,
            measurement = measurements.promotion,
        }) {
            return `${
                measurement === measurements.promotion ? promotionCurrent : productCurrent
            } / ${total}`;
        },

        getPeriod({ startDate, endDate }) {
            const formattedStartDate = this.dateShorthand(startDate);
            const formattedEndDate = this.dateShorthand(endDate);
            return `${formattedStartDate} - ${formattedEndDate}`;
        },

        getValue({ value }) {
            return this.currencyRounded(value);
        },

        getCategoryNames({ hierarchy }) {
            return join(map(hierarchy, 'levelEntryDescription'), ', ');
        },

        totalValueHandler() {
            const { storeGroupResources } = this.stagingArea[this.computedNamespace];
            let value = 0;
            if (storeGroupResources) {
                value = storeGroupResources.reduce((acc, item) => acc + item.value, 0);
            }
            this.setField('value', value);
            return this.currencyRounded(value);
        },
        totalCurrentAllocationsHandler(commitment) {
            const total = this.totalAllocationsHandler();
            this.setField('total', total);
            return this.getTotalCurrentAllocations({ ...commitment, total });
        },

        totalAllocationsHandler() {
            const { storeGroupResources } = this.stagingArea[this.computedNamespace];
            let total = 0;
            if (storeGroupResources) {
                total = storeGroupResources.reduce(
                    (totalAcc, storeGroupResource) =>
                        totalAcc +
                        storeGroupResource.resources.reduce((acc, resource) => {
                            return (
                                acc +
                                resource.instances.reduce(
                                    (noOfOccurrencesSum, instance) =>
                                        noOfOccurrencesSum + instance.noOfOccurrences,
                                    0
                                )
                            );
                        }, 0),
                    0
                );
            }
            return total;
        },

        supplierNameChange(supplierCommitment, newName) {
            this.$emit('supplierNameChange', newName);
        },

        measurementChange() {
            this.$refs.commitmentMatrix[0].updateMeasurementCounts();
        },

        matrixUpdated(supplierCommitment, newWidth) {
            this.$emit('matrix-updated', newWidth);
        },

        postNote(note) {
            this.addNote({
                note,
                id: this.editContext._id,
                fieldKey: NotesFieldKeys.root,
            });
        },
        removeNote(noteId) {
            this.deleteNote({
                noteId,
                id: this.editContext._id,
                fieldKey: NotesFieldKeys.root,
            });
        },
        saveNote(note) {
            this.updateNote({
                note,
                id: this.editContext._id,
                fieldKey: NotesFieldKeys.root,
            });
        },
    },
};
</script>

<style scoped lang="scss">
@import '@style/base/_variables.scss';

.supplier-commitments-form {
    display: contents;
    &__form {
        display: contents;
    }
    &__grid-cell {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        padding: 0.6rem 1rem;
        background-color: $promo-white;
        border-top: $promo-grey 0.1rem solid;
        font-weight: 400;
        line-height: 3rem;
        font-size: 1.2rem;
        color: $promo-text-colour;
        letter-spacing: 0;

        :deep(.v-btn--disabled.v-btn--variant-elevated .v-btn__overlay) {
            opacity: 0;
        }

        :deep(.v-theme--light.v-label) {
            color: $promo-text-colour;
        }

        :deep(.error--text) {
            font-weight: 400;
        }
    }

    &__grid-cell-select {
        :deep(.v-field__input) {
            padding-bottom: 0;
            padding-top: 0;
        }

        :deep(.v-messages__message) {
            background-color: $promo-white;
        }
    }

    & &__grid-cell:first-child {
        font-weight: 600;
    }

    &__radio-group {
        :deep(.v-input--selection-controls) {
            margin: 0;
            padding: 0;
        }

        :deep(.v-input--selection-controls__input) {
            margin-right: 0;
            width: 1.8rem;

            .v-icon.v-icon {
                font-size: 1.4rem;
            }
        }
    }

    &__expanded-area {
        padding: 0 1rem 0.6rem;
        background-color: $promo-white;
        grid-column: 1 / last-column;
    }

    &__expanded-component {
        display: contents;
    }
}
</style>
